import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const recruitingCourses = [
  {
    id: 1,
    status: '모집중',
    title: '기초반 (Level 1)',
    period: '2025.04.07 ~ 2025.05.02',
    duration: '4주 · 주2회 · 총 16시간',
    seats: '잔여 3석',
    icon: 'ri-seedling-line',
    urgent: true,
  },
  {
    id: 2,
    status: '모집중',
    title: '심화반 (Level 2)',
    period: '2025.05.07 ~ 2025.06.04',
    duration: '4주 · 주2회 · 총 16시간',
    seats: '잔여 5석',
    icon: 'ri-bar-chart-line',
    urgent: false,
  },
  {
    id: 3,
    status: '사전접수',
    title: '마스터반 (Level 3)',
    period: '2025.06.09 ~ 2025.07.04',
    duration: '4주 · 주2회 · 총 16시간',
    seats: '사전접수 중',
    icon: 'ri-award-line',
    urgent: false,
  },
];

export default function QuickBanner() {
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-12 md:py-16 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-5 md:px-6">
        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-center justify-between mb-8 gap-3 anim-fade-up ${isVisible ? 'is-visible' : ''}`}>
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-[#C9A84C] rounded-full flex-shrink-0" />
            <div>
              <h2 className="text-lg md:text-xl font-black text-[#1B3A6B]">2027 국가자격증 대비반</h2>
              <p className="text-gray-400 text-xs mt-0.5">2026년 상반기 수강생을 모집합니다</p>
            </div>
          </div>
          <Link
            to="/academy"
            className="whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3A6B] hover:text-[#C9A84C] transition-colors cursor-pointer"
          >
            전체 과정 보기 <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Course Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {recruitingCourses.map((course, i) => (
            <div
              key={course.id}
              className={`relative rounded-xl border p-5 transition-all cursor-pointer group card-lift anim-fade-up ${isVisible ? 'is-visible' : ''} ${
                course.urgent
                  ? 'border-[#C9A84C]/40 bg-[#C9A84C]/5'
                  : 'border-gray-100 bg-[#F4F6FA]'
              }`}
              style={{ transitionDelay: isVisible ? `${100 + i * 100}ms` : '0ms' }}
            >
              {/* Status Badge */}
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-sm ${
                  course.status === '모집중'
                    ? 'bg-[#1B3A6B] text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {course.status}
                </span>
                {course.urgent && (
                  <span className="text-xs font-bold text-red-500 flex items-center gap-1">
                    <i className="ri-alarm-warning-line" /> {course.seats}
                  </span>
                )}
                {!course.urgent && (
                  <span className="text-xs text-gray-400">{course.seats}</span>
                )}
              </div>

              {/* Icon + Title */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1B3A6B]/10 text-[#1B3A6B] flex-shrink-0">
                  <i className={`${course.icon} text-lg`} />
                </div>
                <h3 className="text-base font-bold text-[#1B3A6B] leading-snug group-hover:text-[#C9A84C] transition-colors">
                  {course.title}
                </h3>
              </div>

              {/* Info */}
              <div className="space-y-1.5 mb-5">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <i className="ri-time-line text-[#C9A84C]" />
                  <span>{course.duration}</span>
                </div>
              </div>

              <Link
                to="/academy#consult"
                className="whitespace-nowrap w-full flex items-center justify-center gap-1.5 py-2.5 rounded-md bg-[#1B3A6B] text-white text-xs font-bold hover:bg-[#15305a] transition-colors cursor-pointer"
              >
                <i className="ri-edit-line" /> 수강 상담 신청
              </Link>
            </div>
          ))}
        </div>

        {/* 수강료 + 자격증 안내 띠 */}
        <div className={`mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 anim-fade-up ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: isVisible ? '400ms' : '0ms' }}>
          <div className="flex items-center gap-3 bg-[#C9A84C]/8 border border-[#C9A84C]/25 rounded-lg px-5 py-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C9A84C]/15 flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-[#C9A84C]" />
            </div>
            <div>
              <p className="text-xs font-bold text-[#1B3A6B]">전 과정 통합 수강료</p>
              <p className="text-base font-black text-[#C9A84C]">300만원 <span className="text-xs font-normal text-gray-500">(Level 1~3 전체 포함)</span></p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-[#1B3A6B]/5 border border-[#1B3A6B]/10 rounded-lg px-5 py-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1B3A6B]/10 flex-shrink-0">
              <i className="ri-award-line text-[#1B3A6B]" />
            </div>
            <div>
              <p className="text-xs font-bold text-[#1B3A6B]">임시 자격증 발급</p>
              <p className="text-sm text-gray-600">교육과정 수료시 <strong className="text-[#1B3A6B]">2026년 8월부터</strong> 임시자격증 발급 예정</p>
            </div>
          </div>
        </div>
        <div className={`mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-[#1B3A6B]/5 border border-[#1B3A6B]/10 rounded-lg px-5 py-3 anim-fade-up ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: isVisible ? '500ms' : '0ms' }}>
          <i className="ri-information-line text-[#1B3A6B] flex-shrink-0" />
          <p className="text-xs text-gray-600 leading-relaxed flex-1">
            <strong className="text-[#1B3A6B]">수강 신청 전 무료 상담을 권장합니다.</strong>&nbsp;
            일정, 분할 납부, 자격증 취득 절차에 대해 전문 상담사가 자세히 안내해 드립니다.
          </p>
          <Link to="/academy#consult" className="whitespace-nowrap text-xs font-bold text-[#C9A84C] hover:underline cursor-pointer">
            상담 신청 &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
}
